import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Trash2, Edit2 } from 'lucide-react';
import { Recipe } from '@/shared/model';
import { RecipeCategoryMap } from '@/stores';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/base';

interface RecipeSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipes: RecipeCategoryMap;
  onSelect: (recipe: Recipe) => void;
  onCreate: (category: string) => void;
  onEdit: (recipe: Recipe, category: string) => void;
  onDelete: (id: string, category: string) => void;
  title?: string;
}

interface FlattenedRecipe extends Recipe {
  _cat: string;
}

export const RecipeSelectorModal: FC<RecipeSelectorModalProps> = ({ 
    isOpen, 
    onClose, 
    recipes, 
    onSelect,
    onCreate,
    onEdit,
    onDelete,
    title
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Flatten recipes for search: recipes is { category: [list] }
  const flatRecipes: FlattenedRecipe[] = Object.entries(recipes).flatMap(([cat, list]) => 
    (list || []).map(r => ({ ...r, _cat: cat }))
  );

  const getRecipeName = (r: FlattenedRecipe) => r.nameKey ? t(r.nameKey, r.name) : t(`db.${r.name}`, r.name);

  const filteredRecipes = flatRecipes.filter(r => {
    const translatedName = getRecipeName(r);
    return !search || translatedName.toLowerCase().includes(search.toLowerCase()) || r.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <ResponsiveModal
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        title={title || t('recipes.selector_title', 'Пакеты услуг (Шаблоны)')}
        className="max-w-lg p-0 overflow-hidden"
        stickyHeader
    >
        <div className="flex flex-col h-full max-h-[80vh]">
            {/* Toolbar */}
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 sticky top-0 z-10 space-y-3 shrink-0">
                <div className="relative">
                    <Search className="absolute left-3 top-3 text-slate-400 dark:text-slate-500" size={18}/>
                    <Input 
                        className="pl-10"
                        placeholder={t('recipes.search_placeholder', 'Поиск шаблона...')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                
                <Button 
                    variant="primary"
                    className="w-full h-12 bg-purple-600 hover:bg-purple-700 shadow-purple-200 dark:shadow-none"
                    onClick={() => { onCreate('wall'); }}
                >
                    <Plus size={18}/> {t('recipes.create_new', 'Создать новый пакет')}
                </Button>
            </div>

            {/* List */}
            <div className="overflow-y-auto p-4 space-y-2 flex-grow bg-slate-50/50 dark:bg-slate-950">
                {filteredRecipes.length === 0 ? (
                    <div className="text-center text-slate-400 dark:text-slate-600 py-10">
                        {search ? t('common.nothing_found', 'Ничего не найдено') : t('recipes.empty_list', 'Нет шаблонов')}
                    </div>
                ) : (
                    filteredRecipes.map(recipe => (
                        <div 
                            key={recipe.id} 
                            className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-md transition-all group"
                        >
                            <div className="flex justify-between items-start gap-3">
                                <button 
                                    onClick={() => { onSelect(recipe); }}
                                    className="flex-1 text-left"
                                >
                                    <div className="font-bold text-slate-800 dark:text-slate-200 group-hover:text-purple-700 dark:group-hover:text-purple-400 transition-colors">
                                        {getRecipeName(recipe)}
                                    </div>
                                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-1 line-clamp-2 font-medium">
                                        {t('recipes.services_count', '{{count}} услуг', { count: recipe.layers.length })}
                                    </div>
                                </button>

                                <div className="flex gap-1 shrink-0">
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onEdit(recipe, recipe._cat); }}
                                        className="p-2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-colors"
                                        title={t('common.edit', 'Редактировать')}
                                    >
                                        <Edit2 size={16}/>
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onDelete(recipe.id, recipe._cat); }}
                                        className="p-2 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors"
                                        title={t('common.delete', 'Удалить')}
                                    >
                                        <Trash2 size={16}/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    </ResponsiveModal>
  );
};
